import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "components/ui/Header";
import Icon from "components/AppIcon";
import RegistrationForm from "./components/RegistrationForm";
import TrustSignals from "./components/TrustSignals";
import SuccessConfirmation from "./components/SuccessConfirmation";
import { supabase } from "lib/supabase";

const INITIAL_FORM = { name: "", dob: "", email: "", mobile: "", school: "", password: "", confirmPassword: "" };

function calcProgress(data) {
  const fields = [data?.name, data?.dob, data?.email, data?.mobile, data?.school, data?.password, data?.confirmPassword];
  const filled = fields?.filter((v) => v?.trim() !== "")?.length;
  return Math.round((filled / fields?.length) * 100);
}

function validate(data, verifiedPhone, verifiedEmail) {
  const errs = {};
  if (!data?.name?.trim()) errs.name = "Full name is required.";
  else if (data?.name?.trim()?.length < 2) errs.name = "Name must be at least 2 characters.";

  if (!data?.dob) {
    errs.dob = "Date of birth is required.";
  } else {
    const dob = new Date(data.dob);
    const today = new Date();
    let age = today?.getFullYear() - dob?.getFullYear();
    const m = today?.getMonth() - dob?.getMonth();
    if (m < 0 || (m === 0 && today?.getDate() < dob?.getDate())) age--;
    if (isNaN(dob?.getTime())) errs.dob = "Enter a valid date.";
    else if (age < 16) errs.dob = "You must be at least 16 years old.";
    else if (age > 100) errs.dob = "Please enter a valid date of birth.";
  }

  if (!data?.email?.trim()) errs.email = "Email address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(data?.email)) errs.email = "Enter a valid email address.";
  else if (!verifiedEmail || verifiedEmail !== data?.email?.trim()) {
    errs.email = "Please verify your email address before registering.";
  }

  if (!data?.mobile?.trim()) {
    errs.mobile = "Mobile number is required.";
  } else if (!verifiedPhone || verifiedPhone !== data?.mobile) {
    errs.mobile = "Please verify your mobile number via SMS before registering.";
  }

  if (!data?.school?.trim()) errs.school = "School or college name is required.";

  if (!data?.password) {
    errs.password = "Password is required.";
  } else if (data?.password?.length < 8) {
    errs.password = "Password must be at least 8 characters.";
  }

  if (!data?.confirmPassword) {
    errs.confirmPassword = "Please confirm your password.";
  } else if (data?.password !== data?.confirmPassword) {
    errs.confirmPassword = "Passwords do not match.";
  }

  return errs;
}

export default function VolunteerRegistration() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registeredVolunteer, setRegisteredVolunteer] = useState(null);
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [submitError, setSubmitError] = useState("");

  const progress = calcProgress(formData);

  const handleChange = useCallback((e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setSubmitError("");
  }, []);

  const handleVerified = useCallback((phone) => {
    setVerifiedPhone(phone);
    if (phone) {
      setErrors((prev) => ({ ...prev, mobile: undefined }));
    }
  }, []);

  const handleEmailVerified = useCallback((email) => {
    setVerifiedEmail(email);
    if (email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate(formData, verifiedPhone, verifiedEmail);
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Sign up with Supabase AUTH — volunteer profile created via DB trigger
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email?.trim(),
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.name?.trim(),
            date_of_birth: formData?.dob,
            mobile: formData?.mobile,
            school_college: formData?.school?.trim(),
            volunteer_status: "new",
          },
        },
      });

      if (authError) {
        setSubmitError(authError?.message || "Registration failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // If trigger doesn't fire (e.g. user already exists in auth but not volunteers), insert directly
      if (authData?.user) {
        const { error: insertError } = await supabase?.from("volunteers")?.upsert({
          id: authData?.user?.id,
          full_name: formData?.name?.trim(),
          date_of_birth: formData?.dob,
          email: formData?.email?.trim(),
          mobile: formData?.mobile,
          school_college: formData?.school?.trim() || null,
          volunteer_status: "new",
        }, { onConflict: "id" });

        if (insertError) {
          console.error("Volunteer profile insert error:", insertError?.message);
        }
      }

      setIsSubmitting(false);
      setRegisteredVolunteer({ ...formData, status: "new", id: authData?.user?.id || Date.now() });
    } catch (err) {
      setSubmitError("Network error. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header isAuthenticated={false} user={null} />
      <main className="flex-1 flex items-start justify-center px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="w-full max-w-lg">
          {/* Page Header */}
          {!registeredVolunteer && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                <Icon name="Heart" size={28} color="var(--color-primary)" />
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-2">
                Become a Volunteer
              </h1>
              <p className="font-body text-sm md:text-base text-muted-foreground">
                Join our community and make a difference. Fill in your details below to get started.
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
                <Link
                  to="/volunteer-dashboard"
                  className="font-caption text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Icon name="LayoutDashboard" size={14} color="currentColor" />
                  Dashboard
                </Link>
                <span className="text-border">|</span>
                <Link
                  to="/training-courses-catalog"
                  className="font-caption text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Icon name="BookOpen" size={14} color="currentColor" />
                  Training Catalog
                </Link>
              </div>
            </div>
          )}

          {/* Card */}
          <div className="bg-card border border-border rounded-xl shadow-md p-6 md:p-8">
            {registeredVolunteer ? (
              <SuccessConfirmation volunteer={registeredVolunteer} />
            ) : (
              <>
                <RegistrationForm
                  formData={formData}
                  errors={errors}
                  progress={progress}
                  isSubmitting={isSubmitting}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  verifiedPhone={verifiedPhone}
                  onVerified={handleVerified}
                  verifiedEmail={verifiedEmail}
                  onEmailVerified={handleEmailVerified}
                  submitError={submitError}
                />
                <TrustSignals />
              </>
            )}
          </div>

          {/* Footer note */}
          {!registeredVolunteer && (
            <p className="font-caption text-xs text-muted-foreground text-center mt-4">
              Already registered?{" "}
              <Link to="/volunteer-dashboard" className="text-primary hover:underline">
                Go to your Dashboard
              </Link>
            </p>
          )}
        </div>
      </main>
      {/* Page Footer */}
      <footer className="border-t border-border bg-card py-4 px-4 md:px-6 lg:px-8">
        <p className="font-caption text-xs text-muted-foreground text-center">
          &copy; {new Date()?.getFullYear()} VolunteerHub. All rights reserved.
        </p>
      </footer>
    </div>
  );
}